import { useState, useEffect, useMemo } from "react";
import Txn from "./Txn";
import { ListHeader, OpenCloseBalance, ShimmerLoader, HeaderShimmer } from "components";
import { transactionAPI } from "services";
import useUniversalStore from "store/universalStore";
import { formatMoney, debounce } from "utils";

const TxnList = ({ animation, setAnimation, hideClosed }) => {
  const { transactions, selectedMonth } = useUniversalStore((state) => state);
  const write = useUniversalStore((state) => state.write);
  const [openBalance, setOpenBalance] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filter transactions based on hideClosed toggle
  const filteredTnx = useMemo(() => {
    if (hideClosed) {
      return transactions.filter((item) => !item.get("cancelled"));
    }
    return transactions;
  }, [hideClosed, transactions]);

  // Calculate daily cash in/out locally
  const dailyCash = useMemo(() => {
    let cashIn = 0;
    let cashOut = 0;

    transactions?.forEach(txn => {
      if (!txn.get("cancelled")) {
        const amount = txn.get("amount") || 0;
        if (amount > 0) cashIn += amount;
        else cashOut += Math.abs(amount);
      }
    });

    return {
      in: formatMoney(cashIn),
      out: formatMoney(cashOut),
      totalDelta: cashIn - cashOut
    };
  }, [transactions]);

  // Derive closing balance from opening balance and daily delta
  const closeBalance = useMemo(() => {
    return openBalance + dailyCash.totalDelta;
  }, [openBalance, dailyCash.totalDelta]);

  // Combined and debounced data fetching
  const debouncedLoadAllData = useMemo(() => {
    const fetchFunc = async (cDate) => {
      if (!cDate) return;
      try {
        const openDate = new Date(cDate.getFullYear(), cDate.getMonth(), cDate.getDate(), 0, 0, 0, 0);

        const [txnsResult, balanceResult] = await Promise.all([
          transactionAPI.getTransactionsByDate(cDate),
          transactionAPI.sumTillDate(openDate)
        ]);

        write({ transactions: txnsResult });
        setOpenBalance(balanceResult);
      } catch (error) {
        console.error("Error loading Cashbook data:", error);
      } finally {
        setLoading(false);
      }
    };
    return debounce(fetchFunc, 800);
  }, []);

  useEffect(() => {
    setLoading(true);
    debouncedLoadAllData(selectedMonth);
  }, [selectedMonth]);

  return (
    <div className={`row background mb-auto pb-5 ${animation}`}
      onAnimationEnd={() => setAnimation("")}>
      {loading ? (
        <>
          <div className="col-6 col-md-6 list-group list-group-flush scrollbar-design h-100 px-3">
            <div className="shimmer-container">
              <HeaderShimmer />
              {[...Array(6)].map((_, i) => <ShimmerLoader key={i} />)}
            </div>
          </div>
          <div className="col-6 col-md-6 list-group list-group-flush scrollbar-design h-100 px-3">
            <div className="shimmer-container">
              <HeaderShimmer />
              {[...Array(6)].map((_, i) => <ShimmerLoader key={i} />)}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="col-6 col-md-6 list-group list-group-flush overflow-scroll scrollbar-design"
            style={{ height: 'calc(100vh - 150px)', maxHeight: 'calc(100vh - 150px)' }}>
            <ListHeader type="in" amount={dailyCash.in} />
            <OpenCloseBalance type="open" sum={openBalance} />
            {filteredTnx
              ?.filter((txn) => txn.get("amount") > 0)
              ?.map((txn, i) => (
                <Txn
                  data={txn}
                  key={txn.id || i}
                  setLoading={setLoading}
                />
              ))}
          </div>
          <div className="col-6 col-md-6 list-group list-group-flush overflow-scroll scrollbar-design"
            style={{ height: 'calc(100vh - 150px)', maxHeight: 'calc(100vh - 150px)' }}>
            <ListHeader type="out" amount={dailyCash.out} />
            {filteredTnx
              ?.filter((txn) => txn.get("amount") < 0)
              ?.map((txn, i) => (
                <Txn
                  data={txn}
                  key={txn.id || i}
                  setLoading={setLoading}
                />
              ))}
            <OpenCloseBalance type="close" sum={closeBalance} />
          </div>
        </>
      )}
    </div>
  );
};

export default TxnList;